import { memo, type ReactNode } from 'react';

import { Close as CloseIcon, EllipsisVertical as EllipsisVerticalIcon } from '@air/next-icons';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { SelectionActionBarItemCount } from '~/components/v2/components/shared/selectionBar/SelectionActionBarItemCount';
import { SELECTION_ACTION_BAR_ID } from '~/constants/selectionActionBar';

export type SelectionActionBarComponentProps = {
  menuOptions: DropdownMenuOption[];
  onClearAll: () => void;
  itemLabel?: string;
  leftBarButtons?: ReactNode;
  stackLeftBarButtons?: boolean;
  omitBoardName?: boolean;
  prefixBarButtons?: ReactNode;
  selectAllTotalCount?: number | null;
  suffixBarButtons?: ReactNode;
};

/**
 * Displays as a sticky panel at the bottom of the content area
 * Will show the first 4 menu options and move the remaining into an ellipsis menu
 */
export const SelectionActionBarComponent = memo(
  ({
    menuOptions,
    onClearAll,
    leftBarButtons,
    stackLeftBarButtons,
    omitBoardName,
    prefixBarButtons,
    selectAllTotalCount,
    suffixBarButtons,
  }: SelectionActionBarComponentProps) => {
    return (
      <div
        className="fade-in slide-in-from-bottom-4 z-0 flex w-full min-w-0 animate-in items-center justify-between gap-4 overflow-hidden rounded-8 bg-grey-12/80 p-4 text-grey-1 backdrop-blur-[18px] duration-300"
        data-testid="SELECTION_ACTION_BAR"
        id={SELECTION_ACTION_BAR_ID}
      >
        {stackLeftBarButtons ? (
          <div
            className="grid min-w-0 shrink-0 grid-cols-[auto,1fr] grid-rows-[auto,auto] items-start gap-x-4 gap-y-0"
            data-testid="SELECTION_ACTION_BAR_LEFT_GROUP"
          >
            <div className="row-span-2 self-center">
              <IconButton
                appearance="ghost"
                className="shrink-0 rounded-4 text-grey-1 hover:text-grey-10 active:text-grey-9"
                color="grey"
                data-testid="SELECTION_ACTION_BAR_CLEAR_BTN"
                icon={CloseIcon}
                label="Clear all"
                onClick={onClearAll}
                size="small"
              />
            </div>
            <div className="self-center">
              <SelectionActionBarItemCount omitBoardName={omitBoardName} selectAllTotalCount={selectAllTotalCount} />
            </div>
            {leftBarButtons ? <div className="flex min-w-0 items-start gap-2">{leftBarButtons}</div> : null}
          </div>
        ) : (
          <div className="flex min-w-0 shrink-0 items-center gap-4" data-testid="SELECTION_ACTION_BAR_LEFT_GROUP">
            <IconButton
              appearance="ghost"
              className="shrink-0 rounded-4 text-grey-1 hover:text-grey-10 active:text-grey-9"
              color="grey"
              data-testid="SELECTION_ACTION_BAR_CLEAR_BTN"
              icon={CloseIcon}
              label="Clear all"
              onClick={onClearAll}
              size="small"
            />
            <SelectionActionBarItemCount omitBoardName={omitBoardName} selectAllTotalCount={selectAllTotalCount} />
            {leftBarButtons ? <div className="flex min-w-0 items-center gap-4">{leftBarButtons}</div> : null}
          </div>
        )}
        <div className="flex min-w-0 shrink items-center justify-end gap-4 overflow-hidden">
          <div className="flex min-w-0 shrink items-center gap-4 overflow-hidden">{prefixBarButtons}</div>

          {menuOptions.length > 0 && (
            <DropdownMenu
              sideOffset={16}
              className="w-[240px]"
              trigger={
                <IconButton
                  appearance="ghost"
                  className="shrink-0 ui2:rounded-4 text-grey-1 hover:text-grey-10 active:text-grey-9"
                  color="grey"
                  data-testid="SELECTION_ACTION_BAR_MORE_ACTIONS"
                  label="More actions"
                  size="small"
                >
                  <EllipsisVerticalIcon className="size-4" />
                </IconButton>
              }
            >
              {renderDropdownItems({ options: menuOptions })}
            </DropdownMenu>
          )}
          <div className="flex shrink-0 gap-2">{suffixBarButtons}</div>
        </div>
      </div>
    );
  },
);

SelectionActionBarComponent.displayName = 'SelectionActionBarComponent';
